import { Button, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ErrorBox, FlexBox } from 'components/Box'
import { BoxForm, NumberField, Switch, TextField } from 'components/Form'
import Tooltip from 'components/Tooltip'
import { StatusChip } from 'features/globalSetup/pages/GoogleAdManagerConnectionPage/components/StatusChip'
import { Dialog } from 'features/globalSetup/pages/GoogleAdManagerConnectionPage/sections/SetupPrebidStackSection/components/Dialog'
import { DATE_FORMAT, formatDate } from 'utils/date'
import { useAccessibility } from '../hooks/useAccessibility'
import { CommonProps } from '../types'
import { Schema } from './schema'

export const Form = ({
  formId,
  gamSetupOrder,
  isDialogOpen,
  setIsDialogOpen,
}: CommonProps): JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupPrebidStack',
  })
  const { control } = useFormContext<Schema>()
  const { isActionDisabled, isSaveButtonDisabled, isSaveButtonVisible } = useAccessibility<Schema>({
    gamSetupOrder,
  })

  return (
    <BoxForm>
      <FlexBox
        axis='x'
        gap={1}
      >
        <Typography>{t('form.instream.header')}</Typography>
        {gamSetupOrder && (
          <Tooltip
            content={
              gamSetupOrder.status === 'completed'
                ? t('form.statusTooltip', {
                    updatedAt: formatDate(gamSetupOrder.updatedAt, DATE_FORMAT.WITH_TIME),
                  })
                : ''
            }
          >
            <StatusChip status={gamSetupOrder.status} />
          </Tooltip>
        )}
      </FlexBox>

      <TextField
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.vastUrl.label')}
        name='vastUrl'
        tooltip={{
          content: t('form.instream.vastUrl.tooltip'),
        }}
      />

      <NumberField
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.duration.label')}
        name='duration'
        tooltip={{
          content: t('form.instream.duration.tooltip'),
        }}
      />

      <Switch
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.jsdelivrEnabled.label')}
        labelPlacement='start'
        name='jsdelivrEnabled'
        tooltip={{
          content: t('form.instream.jsdelivrEnabled.tooltip'),
        }}
      />

      {gamSetupOrder && gamSetupOrder.status === 'failed' && (
        <ErrorBox errorMessage={gamSetupOrder.errorMessage} />
      )}

      {isSaveButtonVisible && (
        <Button
          disabled={isSaveButtonDisabled}
          onClick={(): void => setIsDialogOpen(true)}
          sx={{ width: 'fit-content' }}
          variant='contained'
        >
          {t('form.start.label')}
        </Button>
      )}

      <Dialog<Schema>
        formId={formId}
        gamSetupOrder={gamSetupOrder}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
      />
    </BoxForm>
  )
}
